﻿'use strict';
import ScrollMagic from "scrollmagic";
import TimelineLite from "./TimelineLite";
import { Linear } from "./TweenLite";
import { Expo } from "./EasePack";
import "./animation.gsap";

export default class ScrollAnimations {
  constructor(props) {
    this.elements = props.elements;
    this.isMobile = props.isMobile;
    this.isMoment = props.isMoment;

    this.controller = new ScrollMagic.Controller();
    this.defaultOptions = {
      element: null,
      animationType: 'parallax',
      xPosition: 'image-right',
      duration: 2,
      isScrollBound: true,
      hasReplay: true,
      triggerHook: 0.2,
      sceneDuration: this.isMobile ? '50%' : '300',
      contentType: 'standard',
      isFirstItem: false,
      offset: 0,
    };

    this.initializeAnimations(this.elements, this.defaultOptions);
  }

  initializeAnimations = (elements, defaultOptions) => {
    if (!(elements && elements.length)) {
      return;
    }

    for (const [index, element] of elements.entries()) {
      let config;
      const isFirstItem = this.isMoment ? false : index === 0;
      const contentType = element.dataset.content || 'standard';
      const animationType = element.dataset.type || 'parallax';
      if (animationType === "none") {
        continue;
      }
      const xPosition = element.classList.contains('alternate')
        ? 'image-left'
        : 'image-right';
      const animationOptions = this.getAnimationOptions(animationType, contentType, isFirstItem);
      config = { ...defaultOptions, ...animationOptions, element, xPosition, contentType, isFirstItem };
      const timeline = this.createTimeline(config);
      if (!timeline) {
        continue;
      }
      this.createScene(timeline, config);
    }
  };

  getAnimationOptions = (animationType, contentType, isFirstItem) => {
    switch (animationType) {
      case "parallax": {

        let sceneDuration;
        if (isFirstItem && contentType === "hero") {
          sceneDuration = this.isMobile ? '300' : '50%';
        }
        else {
          sceneDuration = this.isMobile ? '600' : '800';
        }

        return {
          isScrollBound: true,
          hasReplay: true,
          triggerHook: isFirstItem ? (this.isMobile ? 0.1 : 0.1) : 0.6,
          animationType,
          sceneDuration,
        };
      }
      case 'gif':
        return {
          animationType,
        };
      case 'sticky':
        return {
          isScrollBound: true,
          triggerHook: 1,
          animationType,
          hasReplay: true,
          sceneDuration: '100%',
        };
      case "fly-in": {
        let triggerHook;
        let sceneDuration;
        if (isFirstItem) {
          triggerHook = 0;
          sceneDuration = 0;
        }
        else {
          triggerHook = this.isMobile ? 0.5 : 0.4;
          sceneDuration = 0;
        }
        return {
          isScrollBound: false,
          triggerHook,
          sceneDuration,
          animationType,
          hasReplay: false,
          offset: this.isMobile ? -105 : -130,
        };
      }
      case "sequence": {
        let triggerHook = this.isMobile ? 0.3 : 0.2;
        let sceneDuration;
        if (isFirstItem) {
          triggerHook = 0.1;
          sceneDuration = this.isMobile ? '300' : '500';
        } else if (contentType === 'hero') {
          sceneDuration = this.isMobile ? '50%' : '500';
        } else {
          sceneDuration = this.isMobile ? '50%' : '200';
        }
        return {
          triggerHook,
          isScrollBound: true,
          hasReplay: true,
          animationType,
          sceneDuration,
        };
      }
      case "slide-in":
        return {
          triggerHook: this.isMobile ? 1 : 0.8,
          isScrollBound: true,
          hasReplay: false,
          animationType,
          duration: 0.5,
        };
      case "roll-up":
        return {
          triggerHook: this.isMobile ? 0.9 : 0.7,
          isScrollBound: true,
          hasReplay: true,
          animationType,
          duration: 2,
        };
      case "swipe-in":
        return {
          triggerHook: this.isMobile ? 0.9 : 0.2,
          isScrollBound: false,
          hasReplay: false,
          animationType,
        };
      default: {
        return {
          isScrollBound: true,
          hasReplay: true,
          triggerHook: isFirstItem ? 0.1 : 0.6,
          animationType,
          sceneDuration: this.isMobile ? '600' : '800',
        };
      }
    }
  };

  createTimeline = config => {
    const parent = config.element;
    const contentType = config.contentType;
    const children = parent && parent.children;
    const { animationType, duration, xPosition, isFirstItem } = config;
    if (!(children && children.length)) {
      console.warn("no children for timeline");
      return;
    }

    let firstLayer;
    let secondLayer;
    let thirdLayer;
    let textLayer;
    let gradientLayer;
    let numberLayer;

    for (let i = 0; i < children.length; i++) {
      if (children[i].dataset.animation === "first-layer") {
        firstLayer = children[i];
        continue;
      }
      if (children[i].dataset.animation === "second-layer") {
        secondLayer = children[i];
        continue;
      }
      if (children[i].dataset.animation === "third-layer") {
        thirdLayer = children[i];
        continue;
      }
      if (children[i].dataset.animation === "text-layer") {
        textLayer = children[i];
        continue;
      }
      if (children[i].dataset.animation === "number") {
        numberLayer = children[i];
        continue;
      }
      if (children[i].dataset.animation === "gradient") {
        gradientLayer = children[i];
        continue;
      }
    }

    let timeline = new TimelineLite();
    timeline.add("Start");
    switch (animationType) {
      case "roll-up":
        if (textLayer) {
          if (this.isMobile) {
            timeline = null;
            break;
          }
          const header = textLayer.querySelector('h2');
          const body = textLayer.querySelector('.d3-c-standard-content-package__description');

          if (!header && !body) {
            timeline = null;
            break;
          }

          header
            && timeline.from(header, 2, { y: "100px", opacity: 0, z: 0.1, rotationZ: 0.01, ease: Linear.easeNone, force3D: true }, "Start");
          body
            && timeline.from(body, 2, { y: "100px", opacity: 0, z: 0.1, rotationZ: 0.01, ease: Linear.easeNone, force3D: true }, "Start+=2");
        }
        break;
      case 'gif':
        timeline = null;
        break;
      case "sticky":
        if (firstLayer) {
          const $img = firstLayer.querySelector('img');

          const angleClass = parent.getAttribute('data-angle') || 'angle--both-right-left';
          $img.classList.add(angleClass);
          timeline = null;
        }
        if (textLayer) {
          const image = parent.previousElementSibling;

          const header = textLayer.querySelector('.d3-c-photo-content-package__header');
          const body = textLayer.querySelector('.d3-c-photo-content-package__description');
          const button = textLayer.querySelector('.d3-o-button');


          if (!header && !body && !button) {
            timeline = null;
            break;
          }

          timeline.fromTo(image, 3, { ease: Linear.easeNone, filter: 'brightness(100%)' }, { ease: Linear.easeNone, filter: 'brightness(25%)' }, 'Start');

          if (!this.isMobile) {
            if (header) {
              header.style.opacity = '0';
              timeline.to(header, 2, { delay: 4, opacity: 1 }, "Start+=2");
            }
            if (body) {
              body.style.opacity = '0';
              timeline.to(body, 2, { delay: 7, opacity: 1 }, "Start+=2");
            }
            if (button) {
              button.style.opacity = '0';
              timeline.to(button, 2, { delay: 10, opacity: 1 }, "Start+=2");
            }
          }
          else {
            textLayer.style.opacity = '0';
            timeline.to(textLayer, 2, { delay: 3, opacity: 1 }, "Start+=2");
          }

        }
        break;
      case "fly-in":
        if (firstLayer) {
          const position = xPosition === "image-right" ? "150%" : "-150%";
          timeline.from(firstLayer, 1, { z: 0.1, rotationZ: 0.01, force3D: true, ease: Expo.easeInOut, x: position }, "Start+=0.5");
        }
        if (secondLayer) {
          (contentType === "hero")
            ? timeline.from(secondLayer, 1, { y: "50px", opacity: 0 }, "Start")
            : timeline.from(secondLayer, duration, { y: "-50px", opacity: 0 }, "Start");
        }
        if (thirdLayer) {
          //timeline.from(thirdLayer, duration, { opacity: 0 }, 'Second');
        }
        break;
      case "fade-in":
        if (firstLayer) {
          timeline.from(firstLayer, duration, { y: "100px", opacity: 0 }, "Start");
        }
        if (secondLayer) {
          timeline.from(secondLayer, duration, { y: "100px", opacity: 0 }, "Start");
        }
        if (thirdLayer) {
          timeline.from(thirdLayer, duration, { y: "100px", opacity: 0 }, "Start");
        }
        if (textLayer) {
          timeline.from(textLayer, duration, { y: "100px", opacity: 0 }, "Start");
        }
        break;
      case "sequence":
        if (!firstLayer || !firstLayer.dataset || !firstLayer.dataset.images) {
          timeline = null;
          break;
        }

        let packageType = contentType === "hero" ? "hero" : "standard-content";
        const images = JSON.parse(firstLayer.dataset.images);
        if (!(images && images.length)) {
          console.warn("no images for sequence");
          break;
        }

        const $img = document.createElement('img');
        const $picture = document.createElement('picture');
        $img.className = `d3-c-${packageType}__sequence d3-c-${packageType}__sequence--${xPosition} img - responsive`;
        $picture.appendChild($img);

        const sequence = firstLayer.appendChild($picture);

        const obj = { curImg: 0 };

        timeline.to(obj, 0.5, {
          curImg: images.length - 1,
          roundProps: "curImg",
          repeat: 0,
          immediateRender: true,
          ease: Linear.easeNone,
          onUpdate() {
            $img.setAttribute('src', images[obj.curImg]);
          }
        });

        break;
      case 'slide-in': {
        const position = xPosition === 'image-right' ? '-40%' : '40%';
        if (firstLayer) {
          timeline.from(firstLayer, duration, { z: 0.1, rotationZ: 0.01, ease: Linear.easeNone, force3D: true, x: position }, "Start")
        }
        if (secondLayer) {
          timeline.from(secondLayer, duration, { z: 0.1, rotationZ: 0.01, ease: Linear.easeNone, force3D: true, x: position }, "Start")
        }
        if (thirdLayer) {
          timeline.from(thirdLayer, duration, { z: 0.1, rotationZ: 0.01, ease: Linear.easeNone, force3D: true, x: position }, "Start")
        }
        if (textLayer) {
          if (this.isMobile) {
            timeline = null;
            break;
          }
          timeline.from(textLayer, duration, { z: 0.1, rotationZ: 0.01, ease: Linear.easeNone, force3D: true, x: position }, "Start")
        }
        break;
      }
      case 'moment': {
        const defaultOptions = { ease: Linear.easeNone, force3d: false };
         if (secondLayer) {
          let secondLayerOptions = { ...defaultOptions, xPercent: '1%', yPercent: '5%'};
          timeline.to(secondLayer, duration, secondLayerOptions, "Start");
        }
        if (thirdLayer) {
          const thirdLayerOptions = { ...defaultOptions, force3d: false };
          const position = { ...thirdLayerOptions, yPercent: '3%' };
          timeline.to(thirdLayer, duration, position, 'Start');
        }
        if (gradientLayer) {
          const layerOptions = { ...defaultOptions, force3d: false };
          const position = { ...layerOptions, yPercent: '1%' };
          timeline.to(gradientLayer, duration, position, 'Start');
        }
        if (numberLayer) {
          const layerOptions = { ...defaultOptions, force3d: false };
          const position = { ...layerOptions, yPercent: '1%' };
          timeline.to(numberLayer, duration, position, 'Start');
        }
      }
      case 'parallax':
      default: {
        const defaultOptions = { z: 0.1, rotationZ: 0.01, ease: Linear.easeNone, force3D: true, scale: isFirstItem ? 1.05 : 1.15, yPercent: isFirstItem ? "2%" : "4%" };
        if (firstLayer) {
          let firstLayerOptions = { ...defaultOptions };
          if (contentType === "hero") {
            if (this.isMobile) {
              firstLayerOptions = { ...defaultOptions, yPercent: '1%', scale: 1.2 };
            }
            else if (xPosition !== 'image-left') {
              firstLayerOptions = { ...defaultOptions, xPercent: isFirstItem ? '-3%' : '-5%' };
            }
          }
          timeline.to(firstLayer, duration, firstLayerOptions, "Start");
        }
        if (secondLayer) {
          let secondLayerOptions = { ...defaultOptions, scale: 1, };

          if (contentType !== "hero") {
            secondLayerOptions = { ...defaultOptions, yPercent: '-5%' }
          }
          else if ((!this.isMobile && xPosition === 'image-left') || this.isMobile) {
            secondLayerOptions = { ...defaultOptions, xPercent: '-5%' }
          }
          timeline.to(secondLayer, duration, secondLayerOptions, "Start");
        }
        if (thirdLayer) {
          const layerOptions = { ...defaultOptions, xPercent: 0, scale: 1, };
          const position = this.isMobile ? { ...layerOptions, yPercent: '3%'} : { ...layerOptions, yPercent: '5%'};
          timeline.to(thirdLayer, duration, position, 'Start');
        }
        if (gradientLayer) {
          const layerOptions = { ...defaultOptions, xPercent: 0, scale: 1, };
          const position = this.isMobile ? { ...layerOptions, yPercent: '1%' } : { ...layerOptions, yPercent: '1%' };
          timeline.to(gradientLayer, duration, position, 'Start');
        }
        if (numberLayer) {
          const layerOptions = { ...defaultOptions, xPercent: 0, scale: 1, };
          const position = this.isMobile ? { ...layerOptions, yPercent: '1%' } : { ...layerOptions, yPercent: '1%' };
          timeline.to(numberLayer, duration, position, 'Start');
        }
      }
    }

    return timeline;
  };

  createOverlay = parent => {
    const newSpan = document.createElement('span');
    newSpan.className = 'd3-o-animation__overlay';
    const appendedOverlay = parent.appendChild(newSpan);
    return appendedOverlay;
  };

  createScene = (timeline, { element, isScrollBound, triggerHook, hasReplay, sceneDuration, offset }) => {
    const sceneOptions = {
      triggerElement: element,
      triggerHook,
      duration: sceneDuration,
      offset,
    };

    const scene = new ScrollMagic.Scene(sceneOptions)

    scene
      .setTween(timeline)
      .addTo(this.controller);

    if (!isScrollBound) {
      scene.reverse(hasReplay);
    }
  };
}
